import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckIcon from "@mui/icons-material/Check";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import DomainIcon from "@mui/icons-material/Domain";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import { useProfileMenu } from "../hooks/useProfileMenu";
import { usePreferencesStore } from "../state/preferencesStore";

export const ProfileMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const translations = {
    openAria: t("profileMenu.open_aria", { defaultValue: "Open profile menu" }),
    workingAt: t("profileMenu.working_at", { defaultValue: "Working at" }),
    rolesTitle: t("profileMenu.roles_title", { defaultValue: "Roles" }),
    rolesNone: t("profileMenu.roles_none", { defaultValue: "No roles" }),
    switchAccount: t("profileMenu.switch_account", {
      defaultValue: "Switch account",
    }),
    switchError: t("profileMenu.switch_error", {
      defaultValue: "Failed to switch account.",
    }),
    myProfile: t("profileMenu.my_profile", { defaultValue: "My Profile" }),
    admin: t("profileMenu.admin", { defaultValue: "Administration" }),
    accountSettings: t("profileMenu.account_settings", {
      defaultValue: "Account Settings",
    }),
    signOut: t("profileMenu.sign_out", { defaultValue: "Sign out" }),
    toggleToLight: t("theme.toggleToLight", {
      defaultValue: "Switch to light mode",
    }),
    toggleToDark: t("theme.toggleToDark", {
      defaultValue: "Switch to dark mode",
    }),
  };

  const {
    initials,
    displayName,
    email,
    currentAccount,
    otherAccounts,
    hasMultipleAccounts,
    roles,
    isAdmin,
    switchAccount,
    isSwitching,
    switchFailed,
    signOut,
  } = useProfileMenu();

  const themeMode = usePreferencesStore((s) => s.theme);
  const setTheme = usePreferencesStore((s) => s.setTheme);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const goTo = (path: string) => {
    handleClose();
    navigate(path);
  };

  const toggleTheme = () =>
    setTheme(themeMode === "light" ? "dark" : "light");

  const roleChips = roles.map((role) => (
    <Chip
      key={role}
      label={role}
      size="small"
      color="primary"
      variant="outlined"
    />
  ));

  const accountSwitchItems = otherAccounts.map((account) => (
    <MenuItem
      key={account.accountId}
      disabled={isSwitching}
      onClick={() => switchAccount(account.accountId)}
    >
      <ListItemIcon>
        <DomainIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={account.accountName}
        secondary={account.roles.join(", ")}
      />
    </MenuItem>
  ));

  return (
    <>
      <Tooltip title={translations.openAria}>
        <IconButton
          onClick={handleOpen}
          aria-label={translations.openAria}
          aria-controls={open ? "profile-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          size="small"
          sx={{ ml: 1 }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: "0.9rem",
              fontWeight: 700,
              bgcolor: "primary.main",
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 340 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", gap: 1.5, alignItems: "center" }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontSize: "1rem",
              fontWeight: 700,
              bgcolor: "primary.main",
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {email}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {currentAccount && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DomainIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                {translations.workingAt}{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {currentAccount.accountName}
                </Box>
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {translations.rolesTitle}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {roleChips.length > 0 ? (
                roleChips
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {translations.rolesNone}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {hasMultipleAccounts && <Divider />}
        {hasMultipleAccounts && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pt: 1, pb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <SwitchAccountIcon fontSize="inherit" />
            {translations.switchAccount}
          </Typography>
        )}
        {hasMultipleAccounts && switchFailed && (
          <Typography variant="caption" color="error" sx={{ px: 2, display: "block" }}>
            {translations.switchError}
          </Typography>
        )}
        {hasMultipleAccounts && accountSwitchItems}

        <Divider />

        <MenuItem onClick={() => goTo("/profile")}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translations.myProfile}</ListItemText>
        </MenuItem>

        {isAdmin && (
          <MenuItem onClick={() => goTo("/admin")}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translations.admin}</ListItemText>
          </MenuItem>
        )}

        {isAdmin && (
          <MenuItem onClick={() => goTo("/account")}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translations.accountSettings}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={toggleTheme}>
          <ListItemIcon>
            {themeMode === "light" ? (
              <DarkModeIcon fontSize="small" />
            ) : (
              <LightModeIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {themeMode === "light"
              ? translations.toggleToDark
              : translations.toggleToLight}
          </ListItemText>
          {themeMode === "dark" && <CheckIcon fontSize="small" color="action" />}
        </MenuItem>

        <Divider />

        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translations.signOut}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
